import type { PdfField } from './types';
import type { SystemField, SystemTextField } from './system-types';

/**
 * Mapper feltene fra et annet system til PDF-komponentene
 * med bevaring av rekkefølge og støtte for tomme kolonner.
 * Fjernet className fra output.
 */
export function mapFieldsToPdfContent(fields: SystemField[]): PdfField[] {
  const pdfFields: PdfField[] = [];
  let i = 0;

  // Hjelpefunksjon for å lage et tomt kolonneobjekt
  const emptyColumn = () => ({ label: '', text: '' });

  while (i < fields.length) {
    const currentField = fields[i];

    // Håndter ikke-tekstfelter
    if (currentField.type === 'header') {
      pdfFields.push({
        type: 'header',
        text: currentField.text,
      });
      i++;
      continue;
    }

    if (currentField.type === 'html') {
      pdfFields.push({
        type: 'view-html',
        label: currentField.label,
        text: currentField.htmlContent,
      });
      i++;
      continue;
    }

    if (currentField.type === 'bullets') {
      pdfFields.push({
        type: 'bullets',
        label: currentField.label,
        items: currentField.items.map((item) => ({
          text: item.text,
          alert: item.alert || false,
        })),
      });
      i++;
      continue;
    }

    // Nå håndterer vi tekstfelter
    if (currentField.type === 'text') {
      // fullWidth felt behandles som vanlig view-text, men uten className
      if (currentField.width === 'fullWidth') {
        pdfFields.push({
          type: 'view-text',
          label: currentField.label,
          text: currentField.text,
        });
        i++;
        continue;
      }

      // maxWidth200 (potensielt tre-kolonne layout)
      if (currentField.width === 'maxWidth200') {
        // Se etter andre og tredje felt av samme type
        const nextIndex = i + 1;
        const thirdIndex = i + 2;

        const nextField =
          nextIndex < fields.length &&
          fields[nextIndex].type === 'text' &&
          (fields[nextIndex] as SystemTextField).width === 'maxWidth200'
            ? (fields[nextIndex] as SystemTextField)
            : null;

        const thirdField =
          thirdIndex < fields.length &&
          fields[thirdIndex].type === 'text' &&
          (fields[thirdIndex] as SystemTextField).width === 'maxWidth200'
            ? (fields[thirdIndex] as SystemTextField)
            : null;

        // Opprett tre-kolonne layout
        pdfFields.push({
          type: 'three-column',
          columns: [
            { label: currentField.label, text: currentField.text },
            nextField ? { label: nextField.label, text: nextField.text } : emptyColumn(),
            thirdField ? { label: thirdField.label, text: thirdField.text } : emptyColumn(),
          ],
        });

        // Oppdater indeks
        i = i + 1 + (nextField ? 1 : 0) + (thirdField ? 1 : 0);
        continue;
      }

      // maxWidth300 (potensielt to-kolonne layout)
      if (currentField.width === 'maxWidth300') {
        // Se etter andre felt av samme type
        const nextIndex = i + 1;

        const nextField =
          nextIndex < fields.length &&
          fields[nextIndex].type === 'text' &&
          (fields[nextIndex] as SystemTextField).width === 'maxWidth300'
            ? (fields[nextIndex] as SystemTextField)
            : null;

        // Opprett to-kolonne layout
        pdfFields.push({
          type: 'two-column',
          leftLabel: currentField.label,
          leftText: currentField.text,
          rightLabel: nextField ? nextField.label : '',
          rightText: nextField ? nextField.text : '',
        });

        // Oppdater indeks
        i = i + 1 + (nextField ? 1 : 0);
        continue;
      }

      // Standard håndtering for andre tekstfelter (uten className)
      pdfFields.push({
        type: 'view-text',
        label: currentField.label,
        text: currentField.text,
      });
      i++;
    }
  }

  return pdfFields;
}
